// =====================================================================
// src/lib/trocas.js — Trocas Futuras: a carga de limpeza por dia, sem DOM
//
// Cópia de `_trocasCalcular`, `_trocasCorWorkload`, `_trocasSugerirFolga`
// e `_trocasFmt*` do Monteiro de Moraes (index.html, 6616–6737). A tela
// (`src/modulos/reservas.js`, aba Trocas Futuras) copia `renderTrocas`
// (6738) e `modalDetalhesTrocas` (6855); aqui mora só a conta.
//
// O que a conta responde: em cada um dos 7 dias da janela, quantas saídas
// (cada saída exige limpeza), quantas chegadas (preparo) e quantos
// TURNOVERS (o mesmo quarto sai e chega no mesmo dia — mais pressão na
// equipe). A carga do dia é saídas + turnovers, e a cor compara os dias
// ENTRE SI dentro da janela: o mais leve é verde, o mais pesado vermelho.
//
// Divergências de domínio, ambas obrigatórias:
//   · lá é `r.status === 'cancelada'` que não conta; aqui `situacao`, e o
//     NO-SHOW (0015, não existe no MdM) também não conta — ninguém entrou,
//     ninguém sai, não há limpeza;
//   · lá "Flat N"; aqui "Quarto N", como na Lista.
// =====================================================================
import { somarDias, fimDaOcupacao } from './reservas.js';

export const JANELA = 7;   // dias a exibir (`_trocasJanela`)

// O dia da semana de uma data ISO. Meio-dia local, como no MdM
// (`new Date(iso+'T12:00:00').getDay()`): o dia da semana de uma DATA não
// depende de fuso, e o meio-dia garante que nenhum fuso a empurre para o
// dia vizinho.
export function diaDaSemana(iso) { return new Date(iso + 'T12:00:00').getDay(); }

// A janela: `n` dias a partir de `ini` (ISO), cada um com o esqueleto que
// `calcularTrocas` preenche.
export function diasDaJanela(ini, n = JANELA) {
  const dias = [];
  for (let i = 0; i < n; i++) {
    const iso = somarDias(ini, i);
    dias.push({ iso, dow: diaDaSemana(iso), saidas: 0, chegadas: 0, turnovers: 0, workload: 0,
      flatsSaida: {}, flatsChegada: {}, detSaidas: [], detChegadas: [] });
  }
  return dias;
}

// Canceladas não contam (MdM). No-show também não: é o cancelamento de
// quem não apareceu.
export function contaNasTrocas(r) {
  return !!r && r.situacao !== 'cancelada' && r.situacao !== 'noshow' && !!r.check_in && !!r.check_out;
}

// `_trocasCalcular` (6634). `hospedeDe(r)` devolve o nome do hóspede
// principal — ou null, e aí sai "—" como lá (`r.nomeHospede || '—'`).
export function calcularTrocas(reservas, ini, { janela = JANELA, hospedeDe = () => null } = {}) {
  const dias = diasDaJanela(ini, janela);
  const idx = Object.fromEntries(dias.map(d => [d.iso, d]));
  for (const r of reservas || []) {
    if (!contaNasTrocas(r)) continue;
    const saida = fimDaOcupacao(r);   // 0096: quem saiu antes saiu NAQUELE dia, não no previsto
    const co = idx[saida], ci = idx[r.check_in];
    if (!co && !ci) continue;
    const numero = (r.unidades && r.unidades.numero) || r.quarto || null;
    const det = {
      reservaId: r.id, flat: numero ? 'Quarto ' + numero : 'Quarto ?', flatNumero: numero, flatId: r.unidade_id,
      hospede: hospedeDe(r) || '—', telefone: telefoneDe(r), plataforma: r.plataforma || '',
      checkIn: r.check_in, checkOut: saida,
    };
    if (co) { co.saidas += 1; co.flatsSaida[r.unidade_id] = true; co.detSaidas.push(det); }
    if (ci) { ci.chegadas += 1; ci.flatsChegada[r.unidade_id] = true; ci.detChegadas.push(det); }
  }
  // Turnovers: quarto com saída E chegada no mesmo dia. A carga soma os
  // dois porque "saída+chegada no mesmo dia é mais crítico" (MdM).
  for (const d of dias) {
    d.turnovers = Object.keys(d.flatsSaida).filter(f => d.flatsChegada[f]).length;
    d.workload = d.saidas + d.turnovers;
  }
  return dias;
}

// O telefone do hóspede principal, se a consulta o trouxe. A Lista embute
// `hospedes(nome,principal)`; a aba de trocas pede `telefone` junto para
// o link do WhatsApp do modal (MdM 6890) — e só quem tem `hospedes` vê.
export function telefoneDe(r) {
  const h = Array.isArray(r.hospedes) ? r.hospedes : [];
  const p = h.find(x => x.principal) || h[0];
  return (p && p.telefone) || '';
}

// `_trocasCorWorkload` (6714): verde no mínimo, vermelho no máximo, amarelo
// no meio; todos iguais → amarelo neutro "Equilibrado".
export function corDaCarga(workload, minW, maxW) {
  if (maxW === minW) return { bg: '#fef9c3', border: '#facc15', text: '#854d0e', emoji: '🟡', label: 'Equilibrado' };
  if (workload <= minW) return { bg: '#dcfce7', border: '#22c55e', text: '#166534', emoji: '🟢', label: 'Tranquilo' };
  if (workload >= maxW) return { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', emoji: '🔴', label: 'Pesado' };
  return { bg: '#fef9c3', border: '#facc15', text: '#854d0e', emoji: '🟡', label: 'Médio' };
}

// `_trocasSugerirFolga` (6742): o dia de menor carga; em empate, prefere
// dia útil (seg–sex); ainda em empate, o primeiro.
export function sugerirFolga(dias) {
  if (!dias || !dias.length) return null;
  const minW = Math.min(...dias.map(d => d.workload));
  const candidatos = dias.filter(d => d.workload === minW);
  const uteis = candidatos.filter(d => d.dow >= 1 && d.dow <= 5);
  return uteis.length ? uteis[0] : candidatos[0];
}

// Os totais dos quatro cards do resumo (renderTrocas, 6743–6745) e os
// extremos que pintam os dias.
export function resumoDasTrocas(dias) {
  const w = dias.map(d => d.workload);
  return {
    saidas: dias.reduce((s, d) => s + d.saidas, 0),
    chegadas: dias.reduce((s, d) => s + d.chegadas, 0),
    turnovers: dias.reduce((s, d) => s + d.turnovers, 0),
    minW: dias.length ? Math.min(...w) : 0,
    maxW: dias.length ? Math.max(...w) : 0,
  };
}

// ── FORMATOS (6728–6740) ─────────────────────────────────────────────
export const DOW = Object.freeze(['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']);
export const DOW_LONGO = Object.freeze(['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado']);
export function fmtDow(dow) { return DOW[dow]; }
export function fmtDowLongo(dow) { return DOW_LONGO[dow]; }
export function fmtDowLongoMaiusculo(dow) { const s = DOW_LONGO[dow]; return s.charAt(0).toUpperCase() + s.slice(1); }
// "26/04"
export function fmtData(iso) { const [, m, d] = iso.split('-'); return `${d}/${m}`; }
// "26/04/2026"
export function fmtDataLonga(iso) { const [a, m, d] = iso.split('-'); return `${d}/${m}/${a}`; }
// "21/09 → 27/09": a etiqueta do card Janela
export function rotuloDoPeriodo(ini, janela = JANELA) { return `${fmtData(ini)} → ${fmtData(somarDias(ini, janela - 1))}`; }
// Ordem por número do quarto, numérica (MdM 6923: localeCompare numeric)
export function ordenarPorQuarto(lista) {
  return lista.slice().sort((a, b) => String(a.flatNumero || '').localeCompare(String(b.flatNumero || ''), undefined, { numeric: true }));
}
// O texto da sugestão de folga (6794): "Segunda-feira, 22/09/2026 tem só 2 trocas"
export function textoDaFolga(d) {
  return { dia: `${fmtDowLongoMaiusculo(d.dow)}, ${fmtDataLonga(d.iso)}`, trocas: `${d.saidas} troca${d.saidas !== 1 ? 's' : ''}` };
}
// O hóspede no card do dia: até 16 letras (6762)
export function hospedeCurto(nome) { const s = String(nome || '—'); return s.length > 16 ? s.slice(0, 16) + '…' : s; }
